import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@common/components';
import { FormRow } from '@common/components/AuthLayout/Layout';
import Error from '@common/components/Error';
import { RHFBlockInputField } from '@common/components/FormElements/BlockInputField';
import { Spinner } from '@common/components/Spinner';
import { useTranslations } from '@lang';
import { EMAIL_REGEXP } from '@utils/validators';

const signInSchema = z.object({
  email: z.string().min(1, 'required').regex(EMAIL_REGEXP, 'email'),
  password: z.string().min(1, 'required'),
});

type SignInFormValues = z.infer<typeof signInSchema>;

type SignInFormProps = {
  onSubmit: (data: SignInFormValues) => void;
  loginError?: string | null;
};

const SignInForm = ({ onSubmit, loginError = null }: SignInFormProps) => {
  const { t } = useTranslations();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  // Workaround: React 19 + RHF v7 — when fields use useController (via
  // RHFBlockInputField), formState.isDirty read in the parent does not trigger
  // re-renders. Calling watch() subscribes the parent to all value changes,
  // ensuring isDirty stays in sync.
  watch();

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ position: 'relative' }}>
      <Spinner showDelayMessage isActive={isSubmitting} />
      {loginError && <Error error={{ code: loginError }} />}
      <FormRow>
        <RHFBlockInputField<SignInFormValues>
          name="email"
          control={control}
          data-testid="email"
          label={t('signIn.emailLabel')}
        />
      </FormRow>
      <FormRow>
        <RHFBlockInputField<SignInFormValues>
          name="password"
          control={control}
          type="password"
          data-testid="password"
          label={t('signIn.passwordLabel')}
        />
      </FormRow>
      <FormRow align="right" actionButtons>
        <Button type="submit" disabled={isSubmitting || !isDirty} isPrimary testId="logIn">
          {t('signIn.login')}
        </Button>
      </FormRow>
    </form>
  );
};

export default SignInForm;
